import Check from "@mui/icons-material/Check";
import CompareArrows from "@mui/icons-material/CompareArrows";
import { Button, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TruncateTypography } from "components/TruncateTypography";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import {
  WasteLinesUnifiedQuery,
  WasteManifestSource,
  WasteStreamDiscrepancy,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { WASTE_LINES_UNIFIED_QUERY } from "routes/Customer/Waste/ManifestLineItems/api";

import { DiscrepanciesCell } from "../../../Manifests/DiscrepanciesCell";
import { SOURCE_LABEL_BY_VALUE, SOURCE_ORDER } from "../../../Manifests/manifestSourceDisplay";
import { SectionHeader } from "../../../Manifests/sections/SectionHeader";
import { SourcesCell } from "../../../Manifests/SourcesCell";
import { WasteStreamAmountCell, WasteStreamAmountHeader } from "../../../WasteStreamAmountCell";
import { WS_DISCREPANCY_FIELD_LABELS } from "../../../wasteStreamDiscrepancyIndicator";
import { WasteStreamFieldWarning } from "../../../WasteStreamFieldWarning";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { buildShippedDateRangeToken } from "./shippedDateRange";

type WasteLineUnifiedRow = NonNullable<
  WasteLinesUnifiedQuery["wasteLinesUnified"]
>["items"][number];

type ManifestLineItemsDataGridProps = {
  wasteStreamId: string;
  /**
   * Builds the href a row points at — used for `getRowLink` (middle-click,
   * open in new tab, accessibility) and as the fallback target for plain
   * left-clicks when no `onLineItemClick` override is provided.
   */
  buildLineItemHref: (lineItemId: string) => string;
  /**
   * Optional override for plain left-clicks on a row. Defaults to navigating
   * to `buildLineItemHref(id)`. Override only when the click should do
   * something other than navigate — e.g. the tenant Waste Streams page
   * appends the line id to the URL so the line-item drawer opens on top of
   * the stream modal instead of replacing it.
   */
  onLineItemClick?: (lineItemId: string) => void;
  /**
   * Parent waste stream's ManifestDiscrepancy rows (any status). Used to
   * render per-cell DiscrepancyCircle indicators on the line-item rows that
   * own a given discrepancy (matched on `wasteLineUnifiedId`). Manifest-level
   * rows (wasteLineUnifiedId NULL) intentionally do not surface here — they
   * aren't tied to a specific line and belong on the manifest detail page.
   */
  discrepancies: ReadonlyArray<WasteStreamDiscrepancy>;
  /**
   * When set, renders a "Compare data sources" button in the grid's top-right
   * toolbar. The dialog itself is owned by the caller (the detail modal).
   */
  onCompareClick?: () => void;
};

export const ManifestLineItemsDataGrid = ({
  wasteStreamId,
  buildLineItemHref,
  onLineItemClick,
  discrepancies,
  onCompareClick,
}: ManifestLineItemsDataGridProps) => {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { isStaff } = useCurrentUser();
  const { featureFlags } = useFeatureFlags();
  // Flag-gated (not staff-gated): customers see the column too, but only their
  // resolved discrepancies (customerView={!isStaff} on the cell). ENG-15929.
  const showDiscrepancies = !!featureFlags[FeatureFlagKey.WasteModule];
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { startDate, endDate } = deferredFilters;

  // Honor single-ended ranges (start.. / ..end), matching the grid behind the
  // modal. Fall back to the current year only when neither bound is set.
  const shippedDateFilter = useMemo(
    () => buildShippedDateRangeToken(startDate, endDate) ?? `${DateTime.now().year}`,
    [endDate, startDate]
  );

  const defaultSearch = useMemo(
    () => `tenantId:${tenantId} wasteStreamId:${wasteStreamId} shippedDate:${shippedDateFilter}`,
    [shippedDateFilter, tenantId, wasteStreamId]
  );

  // Group the stream's discrepancies by line so each row's renderCell can do
  // a single Map lookup instead of scanning the full list. Manifest-level
  // rows (wasteLineUnifiedId NULL) drop out here on purpose.
  const discrepanciesByLineId = useMemo(() => {
    const map = new Map<string, WasteStreamDiscrepancy[]>();
    for (const d of discrepancies) {
      if (!d.wasteLineUnifiedId) continue;
      const arr = map.get(d.wasteLineUnifiedId);
      if (arr) {
        arr.push(d);
      } else {
        map.set(d.wasteLineUnifiedId, [d]);
      }
    }
    return map;
  }, [discrepancies]);

  const columns: OmnisearchGridColDef<WasteLineUnifiedRow>[] = useMemo(
    () => [
      {
        field: "lineItem",
        headerName: "Line Item",
        minWidth: 100,
        flex: 2,
        renderCell: ({ row: { willNotReport, wasteStreamWillNotReport }, value }) => {
          // Mirror the top-level page: surface the source of exclusion so
          // users can tell line-level from stream-level.
          let label: string | null = null;
          if (willNotReport === true) {
            label = "Not Reportable";
          } else if (wasteStreamWillNotReport === true) {
            label = "Not Reportable (stream)";
          }
          return (
            <Box>
              <TruncateTypography variant="body2">{value}</TruncateTypography>
              {label && (
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "profileNumber",
        headerName: "Profile #",
        flex: 1,
        minWidth: 110,
        renderCell: ({ value }) => {
          return <TruncateTypography variant="body2">{value}</TruncateTypography>;
        },
      },
      {
        field: "manifestTrackingNumber",
        headerName: "Manifest #",
        flex: 1,
        minWidth: 120,
        renderCell: ({ value }) => {
          return <TruncateTypography variant="body2">{value}</TruncateTypography>;
        },
      },
      {
        field: "wasteLineNumber",
        headerName: "Line #",
        minWidth: 70,
        flex: 0.5,
        renderCell: ({ value }) => {
          return <TruncateTypography variant="body2">{value}</TruncateTypography>;
        },
      },
      {
        // Which data pillars (E/V/D) this line has on file.
        field: "sources",
        headerName: "Sources",
        minWidth: 90,
        flex: 0.5,
        sortable: false,
        renderCell: ({ row }) => <SourcesCell availableSources={row.availableSources ?? []} />,
      },
      {
        // Single consolidated Amount column — mirrors the parent waste-streams
        // grid. Max across reported sources. Not filterable/sortable because
        // it's a per-row derived value.
        field: "amount",
        headerName: "Amount",
        minWidth: 100,
        flex: 1,
        sortable: false,
        filterable: false,
        renderHeader: () => <WasteStreamAmountHeader />,
        // WasteStreamAmountCell surfaces the per-source amounts on hover when the
        // raw values differ; the (!) flags a persisted quantity discrepancy the
        // amount cell can't (one whose consolidated amounts happen to match).
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            <WasteStreamAmountCell
              vendorAmount={row.vendorAmount}
              eManifestAmount={row.eManifestAmount}
              documentAmount={row.documentAmount}
            />
            <WasteStreamFieldWarning
              discrepancies={discrepanciesByLineId.get(String(row.id)) ?? []}
              field="quantity"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.quantity}
              inline
              isStaff={isStaff}
            />
          </Box>
        ),
      },
      {
        field: "shippedDate",
        headerName: "Ship Date",
        filterKeyType: "date",
        flex: 1,
        minWidth: 110,
        valueGetter: (_, row) => {
          return getUtcDisplayDate(row.shippedDate, "med");
        },
        renderCell: ({ value }) => {
          return <TruncateTypography variant="body2">{value}</TruncateTypography>;
        },
      },
      {
        field: "dotDescription",
        headerName: "DOT Description",
        flex: 1,
        minWidth: 150,
        renderCell: ({ value }) => {
          return <TruncateTypography variant="body2">{value}</TruncateTypography>;
        },
      },
      {
        field: "managementMethodCode",
        headerName: "Management Method Code",
        flex: 1,
        minWidth: 200,
        renderCell: ({ value, row }) => (
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            <TruncateTypography variant="body2">{value}</TruncateTypography>
            <WasteStreamFieldWarning
              discrepancies={discrepanciesByLineId.get(String(row.id)) ?? []}
              field="managementMethodCode"
              fieldLabel={WS_DISCREPANCY_FIELD_LABELS.managementMethodCode}
              sourceValues={{
                [WasteManifestSource.Vendor]: row.vendorManagementMethodCode,
                [WasteManifestSource.EManifest]: row.eManifestManagementMethodCode,
                [WasteManifestSource.ManifestDocument]: row.documentManagementMethodCode,
              }}
              inline
              isStaff={isStaff}
            />
          </Box>
        ),
      },
      {
        field: "vendor",
        headerName: "Vendor",
        flex: 1,
        minWidth: 100,
        filterKeyType: "wasteVendor",
        renderCell: ({ value }) => {
          return <TruncateTypography variant="body2">{value}</TruncateTypography>;
        },
      },
      {
        field: "destinationFacilityName",
        headerName: "TSDF",
        flex: 1,
        minWidth: 100,
        renderCell: ({ value }) => {
          return <TruncateTypography variant="body2">{value}</TruncateTypography>;
        },
      },
      ...(showDiscrepancies
        ? [
            {
              // Mirrors the established Discrepancies column on the manifest
              // listing and manifest-line-items grids — two-bubble layout that
              // renders nothing when both totals are zero.
              field: "discrepancies" as const,
              headerName: "Discrepancies",
              flex: 0.8,
              minWidth: 112,
              sortable: false,
              filterable: false,
              renderCell: ({ row }: { row: WasteLineUnifiedRow }) => (
                // Customers see only resolved discrepancies (ENG-15854).
                <DiscrepanciesCell tally={row.discrepancyTally} customerView={!isStaff} />
              ),
            },
          ]
        : []),
      {
        field: "willNotReport",
        headerName: "Not Reportable",
        filterKeyType: "boolean",
        sortable: false,
        flex: 1,
        minWidth: 140,
        renderCell: ({ value }) => {
          if (value === true) {
            return <Check />;
          }
          return null;
        },
      },
    ],
    [discrepanciesByLineId, showDiscrepancies, isStaff]
  );

  return (
    <Stack spacing={2}>
      <SectionHeader title="Manifest line items" />
      <OmnisearchDataGrid
        withPadding={false}
        persistenceKey="waste-stream-details-manifest-line-items"
        columns={columns}
        dataQuery={WASTE_LINES_UNIFIED_QUERY}
        getItems={(data) => data.wasteLinesUnified?.items ?? []}
        getCount={(data) => data.wasteLinesUnified?.count ?? 0}
        defaultSearch={defaultSearch}
        getRowLink={(row) => buildLineItemHref(String(row.id))}
        onRowClick={(row) => {
          const rowId = String(row.id);
          if (onLineItemClick) {
            onLineItemClick(rowId);
          } else {
            navigate(buildLineItemHref(rowId));
          }
        }}
        noDataMessage="No manifest line items found."
        commandButtons={({ search }) => [
          ...(onCompareClick
            ? [
                <Button
                  key="compare"
                  size="small"
                  startIcon={<CompareArrows />}
                  onClick={onCompareClick}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Compare data sources
                </Button>,
              ]
            : []),
          <ExportDataButton
            key="export"
            exportType="wasteManifestLines"
            fileName="waste-stream-manifest-line-items"
            tenantId={tenantId}
            filter={{ search }}
            iconOnly
          />,
        ]}
        initialSortModel={[{ field: "shippedDate", sort: "desc" }]}
        rowHeight="auto"
        disableRowSelectionOnClick
        showFilters
        showManageColumns
        // No-op auto-generated free-text filters the backend never parses —
        // suppressed per ENG-15798 (same SourcesCell no-op as Manifest Review).
        excludeFilterColumns={["sources"]}
        additionalFilterColumns={[
          {
            header: "Has Vendor Data",
            key: "isVendor",
            filterKeyType: "boolean",
          },
          {
            header: "e‑Manifest",
            key: "isEManifest",
            filterKeyType: "boolean",
          },
          {
            // Multi-select; AND/superset — a line must have ALL chosen sources on file
            // (mirrors the Manifest Review sources filter). ENG-15798.
            header: "Sources",
            key: "sources",
            filterKeyType: "enumAutocomplete",
            enumValues: [...SOURCE_ORDER],
            enumPresentationFunction: (value) => SOURCE_LABEL_BY_VALUE.get(value) ?? value,
          },
        ]}
        initialPageSize={5}
        pageSizeOptions={[5, 10, 25]}
        hideFooter={false}
        columnVisibilityModel={{
          willNotReport: false,
        }}
      />
    </Stack>
  );
};
